import { useState } from 'react'

import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Popover, Tooltip } from '@b-stock/fe-core/bstock-react'

import type { PanelAction } from '../types'
import { TooltipLabel } from '../ui'
import { useIsRowVisible } from '../useIsRowVisible'
import { IconButton, StatusPanel } from './ui'

/**
 * One action, as an icon-only button with a tooltip carrying its label and
 * description.
 *
 * The tooltip is what supplies the description to assistive technology:
 * fe-core `Tooltip` sets `aria-describedby` on the button while it is open,
 * and opens on focus as well as hover. So the label is the accessible name and
 * the description supports it, rather than the two running together into one
 * long name. It also portals out of the cell, which is what gets it past AG
 * Grid's cell clipping.
 *
 * While the action is answering a click, a spinner stands in for the icon, so
 * the button keeps its size and its neighbors keep their places. An answer
 * that is a message (e.g. "No manifest for this lot") opens in a popover
 * anchored to the button, and follows the same portal-and-hide rules as the
 * dropdown.
 */
export const ActionIconButton = ({ action }: { action: PanelAction }) => {
  const {
    description,
    dismissStatusMessage,
    icon,
    isAvailable,
    isPending,
    label,
    onClick,
    statusMessage,
  } = action

  // The tooltip positions against the button, so it needs the element itself.
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null
  )
  const isRowVisible = useIsRowVisible(statusMessage ? buttonElement : null)

  return (
    <>
      <IconButton
        aria-busy={isPending}
        aria-disabled={!isAvailable}
        aria-label={label}
        onClick={() => {
          // Swallowed rather than prevented by the `disabled` attribute, which
          // would also cost the tooltip and keyboard focus.
          if (isAvailable && !isPending) onClick()
        }}
        ref={setButtonElement}
        type="button"
      >
        <FontAwesomeIcon
          icon={isPending ? faSpinnerThird : icon}
          spin={isPending}
        />
      </IconButton>

      {!statusMessage && (
        <Tooltip referenceElement={buttonElement}>
          <TooltipLabel>{label}</TooltipLabel>
          {description}
        </Tooltip>
      )}

      <Popover
        open={!!statusMessage}
        options={{ placement: 'bottom' }}
        referenceElement={buttonElement}
        setOpen={(isOpen) => {
          if (!isOpen) dismissStatusMessage()
        }}
      >
        <StatusPanel $isRowVisible={isRowVisible} role="status">
          {statusMessage}
        </StatusPanel>
      </Popover>
    </>
  )
}
